import React from "react";

import { ListResponse, PathParamsWithId, ResponseWithId } from "commons/types/api/common";
import { OptionsType, ResponseType } from "commons/types/http";

import useHttpManaged from "hooks/useHttpManaged";

import { PartnerBody, PartnerDetails, PartnerListItem } from "types/api/partner";
import { AdvancedListRequest } from "types/api/commons";

function usePartnerApi() {
    const { get, del, post, put } = useHttpManaged();

    const getAllPartners = React.useCallback(
        (queryParams: AdvancedListRequest, options?: OptionsType): ResponseType<ListResponse<PartnerListItem>> => {
            return get<undefined, AdvancedListRequest, ListResponse<PartnerListItem>>({
                url: "/partner",
                queryParams: queryParams,
                options: options,
            });
        },
        [get],
    );

    const getOnePartner = React.useCallback(
        (pathParams: PathParamsWithId, options?: OptionsType): ResponseType<PartnerDetails> => {
            return get<PathParamsWithId, undefined, PartnerDetails>({
                url: "/partner/:id",
                pathParams: pathParams,
                options: options,
            });
        },
        [get],
    );

    const createPartner = React.useCallback(
        (body: PartnerBody, options?: OptionsType): ResponseType<ResponseWithId> => {
            return post<undefined, undefined, PartnerBody, ResponseWithId>({
                url: "/partner",
                body: body,
                options: options,
            });
        },
        [post],
    );

    const updatePartner = React.useCallback(
        (pathParams: PathParamsWithId, body: PartnerBody, options?: OptionsType): ResponseType<null> => {
            return put<PathParamsWithId, undefined, PartnerBody, null>({
                url: "/partner/:id",
                pathParams: pathParams,
                body: body,
                options: options,
            });
        },
        [put],
    );

    const deletePartner = React.useCallback(
        (pathParams: PathParamsWithId, options?: OptionsType): ResponseType<null> => {
            return del<PathParamsWithId, undefined, null>({
                url: "/partner/:id",
                pathParams: pathParams,
                options: options,
            });
        },
        [del],
    );

    return {
        getAllPartners,
        getOnePartner,
        createPartner,
        updatePartner,
        deletePartner,
    };
}

export default usePartnerApi;
